import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { API } from '../../utils/config';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { handleUnauthorizedStatus } from '../../utils/sessionRedirect';
import { currentSite, tokenForSite } from '../../utils/brandSession';
import { 
  CalendarX, 
  CalendarRange, 
  Search, 
  RefreshCw, 
  Filter, 
  X, 
  Home,
  Clock,
  Building2,
  ChevronUp,
  ChevronDown,
  ArrowUpDown,
  Calendar,
  History,
  Plus,
  PencilLine,
  UserRound,
  Ban
} from 'lucide-react';
import { ConfirmAction } from '../../components/home-smart/ConfirmAction';
import { CancelledItems } from '../../components/home-smart/CancelledItems';
import { cancellationApi, useCancelled } from '../../utils/homeSmartCancellations';
import { hsError } from '../../utils/homeSmartCollaborators';


const ChiusureResponsabile = () => {
  const [closures, setClosures] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [activeTab, setActiveTab] = useState('future'); // 'future' o 'past'
  
  // Filtri
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProperty, setSelectedProperty] = useState('all');
  const [dateRangeFilter, setDateRangeFilter] = useState('all');
  
  // Ordinamento
  const [sortBy, setSortBy] = useState('start_date');
  const [sortOrder, setSortOrder] = useState('asc');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [savingClosure, setSavingClosure] = useState(false);
  const [closureForm, setClosureForm] = useState({
    property_id: '',
    mode: 'manual',
    start_date: '',
    end_date: '',
    reason: ''
  });

  const token = localStorage.getItem('hw_token');
  const isHomeSmart = useMemo(() => {
    try {
      return !!JSON.parse(localStorage.getItem('hw_user') || '{}').is_external_tenant || localStorage.getItem('home_smart_context') === 'true';
    } catch {
      return localStorage.getItem('home_smart_context') === 'true';
    }
  }, []);
  const cancelled = useCancelled(isHomeSmart);

  const cancelClosure = async (closure) => {
    try {
      await cancellationApi.cancel('closures', closure.id);
      toast.success('Chiusura annullata: la trovi nella scheda «Annullate»');
      setClosures((current) => current.filter((c) => c.id !== closure.id));
      await cancelled.reload();
    } catch (error) {
      toast.error(hsError(error));
      return false;
    }
  };

  const fetchData = useCallback(async () => {
    const session = {site: currentSite(), token};
    setLoading(true);
    try {
      const [closuresRes, propertiesRes] = await Promise.all([
        fetch(`${API}/api/responsible/my-closures`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API}/api/responsible/my-properties`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      if (handleUnauthorizedStatus(closuresRes.status, session) || handleUnauthorizedStatus(propertiesRes.status, session)) return;
      if (session.site !== currentSite() || session.token !== tokenForSite(session.site)) return;
      if (!closuresRes.ok || !propertiesRes.ok) throw new Error('Impossibile caricare le chiusure');

      const closuresData = await closuresRes.json();
      const propertiesData = await propertiesRes.json();
      setClosures(closuresData);
      setProperties(propertiesData);
    } catch (error) {
      console.error('Error fetching closures:', error);
      toast.error(error.message || 'Impossibile caricare le chiusure');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Normalizza il motivo per la visualizzazione
  const normalizeReason = (closure) => {
    const reason = closure?.reason;
    if (!reason || reason === 'Non specificato' || reason === 'Importato da Excel' || reason === '') {
      return closure?.closure_type === 'office_initiated' ? 'Chiusura manuale' : 'Uso proprietario';
    }
    return reason;
  };

  const getClosureTypeLabel = (closure) => (
    closure.closure_type === 'office_initiated' ? 'Manuale' : 'Proprietario'
  );

  const resetClosureForm = () => setClosureForm({
    property_id: properties[0]?.id || '',
    mode: 'manual',
    start_date: '',
    end_date: '',
    reason: ''
  });

  const openCreateDialog = () => {
    resetClosureForm();
    setShowCreateDialog(true);
  };

  const createClosure = async (event) => {
    event.preventDefault();
    const session = {site: currentSite(), token};
    if (!closureForm.property_id || !closureForm.start_date || !closureForm.end_date) {
      toast.error('Seleziona immobile e date della chiusura');
      return;
    }
    if (closureForm.end_date < closureForm.start_date) {
      toast.error('La data finale non può precedere quella iniziale');
      return;
    }

    setSavingClosure(true);
    try {
      const response = await fetch(`${API}/api/responsible/my-closures`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          property_id: closureForm.property_id,
          start_date: `${closureForm.start_date}T00:00:00Z`,
          end_date: `${closureForm.end_date}T23:59:59Z`,
          closure_type: closureForm.mode === 'owner' ? 'property_initiated' : 'office_initiated',
          reason: closureForm.reason.trim() || null
        })
      });
      const data = await response.json().catch(() => ({}));
      if (handleUnauthorizedStatus(response.status, session)) return;
      if (session.site !== currentSite() || session.token !== tokenForSite(session.site)) return;
      if (!response.ok) {
        const detail = typeof data.detail === 'string' ? data.detail : 'Inserimento non riuscito';
        throw new Error(detail);
      }
      toast.success('Chiusura inserita');
      setShowCreateDialog(false);
      await fetchData();
    } catch (error) {
      toast.error(error.message || 'Inserimento non riuscito');
    } finally {
      setSavingClosure(false);
    }
  };

  // Determina lo stato della chiusura
  const getClosureStatus = (closure) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const start = new Date(closure.start_date);
    const end = new Date(closure.end_date);
    
    if (end < today) return 'past';
    if (start <= today && end >= today) return 'active';
    return 'upcoming';
  };

  // Filtra e ordina le chiusure
  const { futureClosures, pastClosures, stats } = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    let filtered = [...closures];

    // Filtro ricerca
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(c => 
        (c.property_name || '').toLowerCase().includes(search) ||
        (c.property_code || '').toLowerCase().includes(search) ||
        normalizeReason(c).toLowerCase().includes(search) ||
        getClosureTypeLabel(c).toLowerCase().includes(search)
      );
    }

    // Filtro immobile
    if (selectedProperty !== 'all') {
      filtered = filtered.filter(c => c.property_id === selectedProperty);
    }

    // Filtro periodo
    if (dateRangeFilter !== 'all') {
      const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      const startOfNextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);
      const endOfNextMonth = new Date(today.getFullYear(), today.getMonth() + 2, 0);
      const startOf3Months = new Date(today.getFullYear(), today.getMonth() + 3, 0);

      if (dateRangeFilter === 'this_month') {
        filtered = filtered.filter(c => {
          const start = new Date(c.start_date);
          const end = new Date(c.end_date);
          return (start <= endOfMonth && end >= startOfMonth);
        });
      } else if (dateRangeFilter === 'next_month') {
        filtered = filtered.filter(c => {
          const start = new Date(c.start_date);
          const end = new Date(c.end_date);
          return (start <= endOfNextMonth && end >= startOfNextMonth);
        });
      } else if (dateRangeFilter === 'next_3_months') {
        filtered = filtered.filter(c => {
          const start = new Date(c.start_date);
          return start <= startOf3Months && start >= today;
        });
      }
    }

    // Ordinamento
    const sortFn = (a, b) => {
      let aVal, bVal;
      
      switch (sortBy) {
        case 'property_name':
          aVal = (a.property_name || '').toLowerCase();
          bVal = (b.property_name || '').toLowerCase();
          break;
        case 'start_date':
          aVal = new Date(a.start_date);
          bVal = new Date(b.start_date);
          break;
        case 'end_date':
          aVal = new Date(a.end_date);
          bVal = new Date(b.end_date);
          break;
        case 'created_at':
          aVal = new Date(a.created_at || a.imported_at || 0);
          bVal = new Date(b.created_at || b.imported_at || 0);
          break;
        case 'reason':
          aVal = normalizeReason(a).toLowerCase();
          bVal = normalizeReason(b).toLowerCase();
          break;
        case 'status':
          const statusOrder = { active: 0, upcoming: 1, past: 2 };
          aVal = statusOrder[getClosureStatus(a)];
          bVal = statusOrder[getClosureStatus(b)];
          break;
        default:
          aVal = new Date(a.start_date);
          bVal = new Date(b.start_date);
      }

      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    };

    // Separa future e passate
    const future = filtered.filter(c => new Date(c.end_date) >= today).sort(sortFn);
    const past = filtered.filter(c => new Date(c.end_date) < today).sort(sortFn);

    // Statistiche
    const active = closures.filter(c => getClosureStatus(c) === 'active').length;
    const upcoming = closures.filter(c => getClosureStatus(c) === 'upcoming').length;
    const pastCount = closures.filter(c => getClosureStatus(c) === 'past').length;

    return {
      futureClosures: future,
      pastClosures: past,
      stats: { total: closures.length, active, upcoming, past: pastCount }
    };
  }, [closures, searchTerm, selectedProperty, dateRangeFilter, sortBy, sortOrder]);

  // Raggruppa per mese
  const groupByMonth = (closureList) => {
    const groups = {};
    closureList.forEach(c => {
      const date = new Date(c.start_date);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const monthName = date.toLocaleDateString('it-IT', { month: 'long', year: 'numeric' });
      
      if (!groups[monthKey]) {
        groups[monthKey] = { name: monthName, closures: [] };
      }
      groups[monthKey].closures.push(c);
    });
    return Object.entries(groups).sort((a, b) => a[0].localeCompare(b[0]));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const getStatusBadge = (closure) => {
    const status = getClosureStatus(closure);
    if (status === 'past') {
      return <Badge variant="secondary" className="bg-gray-100 text-gray-600">Passata</Badge>;
    } else if (status === 'active') {
      return <Badge className="bg-red-100 text-red-700">In Corso</Badge>;
    } else {
      return <Badge className="bg-blue-100 text-blue-700">Programmata</Badge>;
    }
  };

  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortBy !== field) return <ArrowUpDown className="w-4 h-4 ml-1 text-gray-400" />;
    return sortOrder === 'asc' ? 
      <ChevronUp className="w-4 h-4 ml-1 text-blue-600" /> : 
      <ChevronDown className="w-4 h-4 ml-1 text-blue-600" />;
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedProperty('all');
    setDateRangeFilter('all');
  };

  const hasActiveFilters = searchTerm || selectedProperty !== 'all' || dateRangeFilter !== 'all';

  // Tabella delle chiusure con raggruppamento per mese
  const ClosuresTable = ({ closureList, showMonthGroups = true }) => {
    const groupedClosures = showMonthGroups ? groupByMonth(closureList) : null;

    if (closureList.length === 0) {
      return (
        <div className="text-center py-16">
          <CalendarX className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-600">Nessuna chiusura trovata</h3>
          <p className="text-gray-400 mt-1">
            {hasActiveFilters ? 'Prova a modificare i filtri' : 'Non ci sono chiusure per i tuoi immobili'}
          </p>
        </div>
      );
    }

    const renderTable = (items) => (
      <table className="w-full">
        <thead className="bg-gray-50 border-b sticky top-0">
          <tr>
            <th 
              className="px-4 py-3 text-left text-sm font-semibold text-gray-600 cursor-pointer hover:bg-gray-100"
              onClick={() => toggleSort('property_name')}
            >
              <div className="flex items-center">
                Immobile <SortIcon field="property_name" />
              </div>
            </th>
            <th 
              className="px-4 py-3 text-left text-sm font-semibold text-gray-600 cursor-pointer hover:bg-gray-100"
              onClick={() => toggleSort('start_date')}
            >
              <div className="flex items-center">
                Inizio <SortIcon field="start_date" />
              </div>
            </th>
            <th 
              className="px-4 py-3 text-left text-sm font-semibold text-gray-600 cursor-pointer hover:bg-gray-100"
              onClick={() => toggleSort('end_date')}
            >
              <div className="flex items-center">
                Fine <SortIcon field="end_date" />
              </div>
            </th>
            <th 
              className="px-4 py-3 text-left text-sm font-semibold text-gray-600 cursor-pointer hover:bg-gray-100"
              onClick={() => toggleSort('status')}
            >
              <div className="flex items-center">
                Stato <SortIcon field="status" />
              </div>
            </th>
            <th 
              className="px-4 py-3 text-left text-sm font-semibold text-gray-600 cursor-pointer hover:bg-gray-100"
              onClick={() => toggleSort('reason')}
            >
              <div className="flex items-center">
                Motivo <SortIcon field="reason" />
              </div>
            </th>
            {cancelled.available && <th className="px-4 py-3 text-right text-sm font-semibold text-gray-600">Azioni</th>}
          </tr>
        </thead>
        <tbody className="divide-y">
          {items.map((closure, idx) => (
            <tr 
              key={closure.id || idx} 
              className="hover:bg-gray-50 transition-colors"
            >
              <td className="px-4 py-3">
                <div className="flex items-center gap-2">
                  <Home className="w-4 h-4 text-gray-400" />
                  <span className="font-medium text-gray-900 notranslate" translate="no">
                    {closure.property_name || 'N/A'}
                  </span>
                </div>
              </td>
              <td className="px-4 py-3">
                <span className="font-medium text-gray-900">{formatDate(closure.start_date)}</span>
              </td>
              <td className="px-4 py-3">
                <span className="font-medium text-gray-900">{formatDate(closure.end_date)}</span>
              </td>
              <td className="px-4 py-3">
                {getStatusBadge(closure)}
              </td>
              <td className="px-4 py-3 text-gray-600 max-w-xs">
                <div className="flex flex-col items-start gap-1">
                  <Badge className={closure.closure_type === 'office_initiated' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-800'} data-testid={`closure-type-${closure.id || idx}`}>
                    {getClosureTypeLabel(closure)}
                  </Badge>
                  <span className="max-w-xs truncate" data-testid={`closure-reason-${closure.id || idx}`}>{normalizeReason(closure)}</span>
                </div>
              </td>
              {cancelled.available && (
                <td className="px-4 py-3 text-right">
                  {closure.source === 'hw_sync' && (
                    <ConfirmAction testId={`cancel-closure-${closure.id}`} label="Annulla" icon={Ban} destructive
                      className="border-red-200 text-red-700 hover:bg-red-50"
                      title="Annullare questa chiusura?"
                      description={`${closure.property_name || 'Immobile'} · ${formatDate(closure.start_date)} → ${formatDate(closure.end_date)}. Le date tornano libere e la chiusura passa nella scheda «Annullate», da cui puoi ripristinarla.`}
                      confirmLabel="Annulla chiusura" onConfirm={() => cancelClosure(closure)} />
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    );

    if (!showMonthGroups) {
      return <div className="overflow-x-auto">{renderTable(closureList)}</div>;
    }

    return (
      <div className="space-y-6">
        {groupedClosures.map(([monthKey, group]) => (
          <div key={monthKey}>
            <div className="flex items-center gap-2 px-4 py-2 bg-blue-50 border-l-4 border-blue-500">
              <Calendar className="w-4 h-4 text-blue-600" />
              <span className="font-semibold text-blue-800 capitalize">{group.name}</span>
              <Badge variant="outline" className="ml-2">{group.closures.length}</Badge>
            </div>
            <div className="overflow-x-auto">
              {renderTable(group.closures)}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-6 px-6 pb-6 pt-16 md:pt-6" data-testid="chiusure-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <CalendarX className="w-7 h-7 text-red-600" />
            {isHomeSmart ? 'Chiusure immobili' : 'Chiusure Proprietà'}
          </h1>
          <p className="text-gray-500 mt-1">
            {isHomeSmart ? 'Blocca manualmente le date o registra un periodo riservato al proprietario' : 'Gestisci le chiusure degli immobili da parte dei proprietari'}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {isHomeSmart && (
            <Button onClick={openCreateDialog} className="bg-blue-700 text-white hover:bg-blue-800" data-testid="open-manual-closure-dialog-button">
              <Plus className="mr-2 h-4 w-4" /> Nuova chiusura
            </Button>
          )}
          <Button 
            onClick={fetchData} 
            variant="outline" 
            className="flex items-center gap-2"
            disabled={loading}
            data-testid="refresh-closures-button"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Aggiorna
          </Button>
        </div>
      </div>

      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl" data-testid="manual-closure-dialog">
          <DialogHeader>
            <DialogTitle data-testid="manual-closure-dialog-title">Inserisci una chiusura</DialogTitle>
            <DialogDescription>Scegli se bloccare semplicemente le date o attribuire il periodo al proprietario.</DialogDescription>
          </DialogHeader>
          <form className="space-y-5" onSubmit={createClosure} data-testid="manual-closure-form">
            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-700">Immobile</label>
              <select
                value={closureForm.property_id}
                onChange={(event) => setClosureForm((current) => ({ ...current, property_id: event.target.value }))}
                className="flex h-10 w-full border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-200"
                data-testid="manual-closure-property-select"
              >
                <option value="">Seleziona immobile</option>
                {properties.map((property) => <option key={property.id} value={property.id} data-testid={`manual-closure-property-${property.id}`}>{property.name}</option>)}
              </select>
            </div>

            <fieldset className="space-y-2">
              <legend className="text-sm font-semibold text-slate-700">Tipo di chiusura</legend>
              <div className="grid gap-3 sm:grid-cols-2">
                <button
                  type="button"
                  onClick={() => setClosureForm((current) => ({ ...current, mode: 'manual' }))}
                  className={`flex min-h-24 items-start gap-3 border p-4 text-left transition-colors ${closureForm.mode === 'manual' ? 'border-blue-700 bg-blue-50 text-blue-950' : 'border-slate-200 bg-white text-slate-700 hover:border-blue-400'}`}
                  data-testid="manual-closure-mode-button"
                >
                  <PencilLine className="mt-0.5 h-5 w-5 shrink-0 text-blue-700" />
                  <span><strong className="block">Chiusura manuale</strong><span className="mt-1 block text-sm">Blocco semplice delle date.</span></span>
                </button>
                <button
                  type="button"
                  onClick={() => setClosureForm((current) => ({ ...current, mode: 'owner' }))}
                  className={`flex min-h-24 items-start gap-3 border p-4 text-left transition-colors ${closureForm.mode === 'owner' ? 'border-blue-700 bg-blue-50 text-blue-950' : 'border-slate-200 bg-white text-slate-700 hover:border-blue-400'}`}
                  data-testid="owner-closure-mode-button"
                >
                  <UserRound className="mt-0.5 h-5 w-5 shrink-0 text-blue-700" />
                  <span><strong className="block">Uso proprietario</strong><span className="mt-1 block text-sm">Periodo attribuito al proprietario.</span></span>
                </button>
              </div>
            </fieldset>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2"><label htmlFor="manual-closure-start" className="text-sm font-semibold text-slate-700">Dal</label><Input id="manual-closure-start" type="date" value={closureForm.start_date} onChange={(event) => setClosureForm((current) => ({ ...current, start_date: event.target.value, end_date: current.end_date && current.end_date < event.target.value ? event.target.value : current.end_date }))} data-testid="manual-closure-start-date-input" /></div>
              <div className="space-y-2"><label htmlFor="manual-closure-end" className="text-sm font-semibold text-slate-700">Al</label><Input id="manual-closure-end" type="date" min={closureForm.start_date || undefined} value={closureForm.end_date} onChange={(event) => setClosureForm((current) => ({ ...current, end_date: event.target.value }))} data-testid="manual-closure-end-date-input" /></div>
            </div>

            <div className="space-y-2">
              <label htmlFor="manual-closure-reason" className="text-sm font-semibold text-slate-700">Nota facoltativa</label>
              <Input id="manual-closure-reason" maxLength={180} value={closureForm.reason} onChange={(event) => setClosureForm((current) => ({ ...current, reason: event.target.value }))} placeholder={closureForm.mode === 'owner' ? 'Es. soggiorno del proprietario' : 'Es. manutenzione'} data-testid="manual-closure-reason-input" />
            </div>

            <DialogFooter className="gap-2 sm:space-x-0">
              <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)} data-testid="cancel-manual-closure-button">Annulla</Button>
              <Button type="submit" className="bg-blue-700 text-white hover:bg-blue-800" disabled={savingClosure} data-testid="save-manual-closure-button">{savingClosure ? 'Salvataggio…' : 'Salva chiusura'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Filtri */}
      <Card className="p-4">
        <div className="flex flex-col gap-4">
          {/* Prima riga: Ricerca e toggle filtri */}
          <div className="flex flex-col md:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Cerca per immobile, codice o motivo..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="closures-search-input"
              />
            </div>
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2"
              data-testid="closures-filter-toggle-button"
            >
              <Filter className="w-4 h-4" />
              Filtri
              {hasActiveFilters && (
                <Badge className="ml-1 bg-blue-600 text-white text-xs">
                  {[searchTerm, selectedProperty !== 'all', dateRangeFilter !== 'all'].filter(Boolean).length}
                </Badge>
              )}
            </Button>
            {hasActiveFilters && (
              <Button variant="ghost" onClick={clearFilters} className="text-gray-500" data-testid="closures-clear-filters-button">
                <X className="w-4 h-4 mr-1" /> Pulisci
              </Button>
            )}
          </div>

          {/* Filtri avanzati */}
          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-3 border-t">
              <div>
                <label className="text-sm font-medium text-gray-600 mb-1 block">Immobile</label>
                <Select value={selectedProperty} onValueChange={setSelectedProperty}>
                  <SelectTrigger data-testid="closures-property-filter">
                    <SelectValue placeholder="Tutti gli immobili" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutti gli immobili</SelectItem>
                    {properties.map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-600 mb-1 block">Periodo</label>
                <Select value={dateRangeFilter} onValueChange={setDateRangeFilter}>
                  <SelectTrigger data-testid="closures-period-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutti i periodi</SelectItem>
                    <SelectItem value="this_month">Questo mese</SelectItem>
                    <SelectItem value="next_month">Prossimo mese</SelectItem>
                    <SelectItem value="next_3_months">Prossimi 3 mesi</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Statistiche */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-600 rounded-lg">
              <CalendarRange className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-sm text-blue-600 font-medium">Totale</p>
              <p className="text-2xl font-bold text-blue-800">{stats.total}</p>
            </div>
          </div>
        </Card>
        <Card className="p-4 bg-gradient-to-br from-red-50 to-red-100 border-red-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-600 rounded-lg">
              <CalendarX className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-sm text-red-600 font-medium">In Corso</p>
              <p className="text-2xl font-bold text-red-800">{stats.active}</p>
            </div>
          </div>
        </Card>
        <Card className="p-4 bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-600 rounded-lg">
              <Clock className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-sm text-amber-600 font-medium">Programmate</p>
              <p className="text-2xl font-bold text-amber-800">{stats.upcoming}</p>
            </div>
          </div>
        </Card>
        <Card className="p-4 bg-gradient-to-br from-gray-50 to-gray-100 border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gray-600 rounded-lg">
              <Building2 className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-sm text-gray-600 font-medium">Immobili</p>
              <p className="text-2xl font-bold text-gray-800">
                {new Set(closures.map(c => c.property_id)).size}
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Disclaimer importante */}
      {!isHomeSmart && <Card className="p-4 bg-amber-50 border-amber-200">
        <div className="flex gap-3">
          <div className="flex-shrink-0">
            <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
              <span className="text-xl">⚠️</span>
            </div>
          </div>
          <div>
            <h3 className="font-semibold text-amber-800 mb-2">Promemoria Importante - Gestione Chiusure</h3>
            <ul className="text-sm text-amber-700 space-y-1.5 list-disc list-inside">
              <li><strong>Prima di ogni chiusura:</strong> l'immobile deve essere pronto e pulito per il proprietario</li>
              <li><strong>Dopo ogni chiusura:</strong> controllare come il proprietario ha lasciato l'immobile</li>
              <li><strong>Se l'immobile è sporco o parzialmente pulito:</strong> scattare foto PRIMA di pulire come prova</li>
              <li><strong>Senza foto in caso di contestazione = Fattura/Ricevuta non pagata</strong></li>
              <li>Ogni pulizia richiede <strong>Fattura/Ricevuta individuale</strong></li>
              <li>Fatture/Ricevute entro il 10 → pagate entro il 20 | Dopo il 10 → mese successivo</li>
            </ul>
          </div>
        </div>
      </Card>}

      {/* Tabs Future/Passate */}
      <div className="flex gap-2 border-b">
        <button
          onClick={() => setActiveTab('future')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            activeTab === 'future'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
          data-testid="future-closures-tab"
        >
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            Chiusure Future
            <Badge className="bg-blue-100 text-blue-700">{futureClosures.length}</Badge>
          </div>
        </button>
        <button
          onClick={() => setActiveTab('past')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            activeTab === 'past'
              ? 'border-gray-600 text-gray-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
          data-testid="past-closures-tab"
        >
          <div className="flex items-center gap-2">
            <History className="w-4 h-4" />
            Chiusure Passate
            <Badge variant="secondary">{pastClosures.length}</Badge>
          </div>
        </button>
        {cancelled.available && (
          <button
            onClick={() => setActiveTab('cancelled')}
            className={`px-4 py-2 font-medium border-b-2 transition-colors ${
              activeTab === 'cancelled'
                ? 'border-red-600 text-red-700'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
            data-testid="cancelled-closures-tab"
          >
            <div className="flex items-center gap-2">
              <Ban className="w-4 h-4" />
              Annullate
              <Badge variant="secondary" data-testid="cancelled-closures-tab-count">{cancelled.closures.length}</Badge>
            </div>
          </button>
        )}
      </div>

      {/* Tabella chiusure */}
      <Card className="overflow-hidden">
        {activeTab === 'cancelled' && cancelled.available ? (
          <CancelledItems kind="closures" rows={cancelled.closures} onChanged={() => Promise.all([fetchData(), cancelled.reload()])} />
        ) : loading ? (
          <div className="flex items-center justify-center py-20">
            <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : (
          <ClosuresTable 
            closureList={activeTab === 'future' ? futureClosures : pastClosures}
            showMonthGroups={activeTab === 'future'}
          />
        )}
        
        {/* Footer con conteggio */}
        {!loading && activeTab !== 'cancelled' && (activeTab === 'future' ? futureClosures : pastClosures).length > 0 && (
          <div className="px-4 py-3 bg-gray-50 border-t text-sm text-gray-500">
            Mostrate {activeTab === 'future' ? futureClosures.length : pastClosures.length} chiusure {activeTab === 'future' ? 'future' : 'passate'}
          </div>
        )}
      </Card>
    </div>
  );
};

export default ChiusureResponsabile;
